import React from 'react';
import { motion } from 'framer-motion';
export function About() {
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.8
    }
  };
  return <div className="bg-white min-h-screen pt-32 pb-20" data-id="element-323">
      {/* Hero Section */}
      <section className="px-6 max-w-[1800px] mx-auto mb-32" data-id="element-324">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end" data-id="element-325">
          <div className="w-full" data-id="element-326">
            <motion.span {...fadeIn} className="text-xs md:text-sm font-display font-bold uppercase tracking-[0.3em] md:tracking-[0.5em] mb-8 block text-black/50" data-id="element-327">
              Our Story
            </motion.span>
            <motion.h1 {...fadeIn} transition={{
            delay: 0.2,
            duration: 0.8
          }} className="text-xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-display font-bold tracking-[0.05em] md:tracking-[0.1em] uppercase leading-[0.9] mb-12" data-id="element-328">
              Modern <br data-id="element-329" /> Heritage
            </motion.h1>
          </div>
          <div className="w-full" data-id="element-330">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2
          }} className="aspect-[16/9] bg-grey-divider overflow-hidden grayscale contrast-125" data-id="element-331">
              <img src="https://cdn.magicpatterns.com/uploads/3Axzh5yjUCYGZvVYAbBuxk/image.png" alt="Djily Creations Heritage" className="w-full h-full object-cover" data-id="element-332" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Vision */}
      <section className="px-6 max-w-[1200px] mx-auto mb-40" data-id="element-333">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-24" data-id="element-334">
          <motion.div {...fadeIn} className="space-y-8" data-id="element-335">
            <h2 className="text-xl sm:text-2xl md:text-4xl font-display font-bold tracking-widest uppercase border-b border-black pb-4 inline-block" data-id="element-336">
              The Vision
            </h2>
            <p className="text-sm md:text-base font-normal tracking-widest text-black/80 leading-relaxed uppercase" data-id="element-337">
              Djily Creations was born from a singular desire: to elevate the
              rich textile traditions of the African continent into the global
              luxury conversation.
            </p>
            <p className="text-sm md:text-base font-normal tracking-widest text-black/60 leading-relaxed uppercase" data-id="element-338">
              We believe that fashion is a silent language—a narrative of
              lineage, resilience, and artistry. Our work is a dialogue between
              the ancestral hands that weave our fabrics and the contemporary
              silhouettes that define the modern wardrobe.
            </p>
          </motion.div>
          <motion.div {...fadeIn} transition={{
          delay: 0.3
        }} className="pt-12 md:pt-32" data-id="element-339">
            <blockquote className="text-xl md:text-2xl font-display italic tracking-widest uppercase leading-tight border-l-4 border-black pl-8 py-4" data-id="element-340">
              "We don't just make clothes; we preserve stories that were meant
              to be worn."
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Pillars / Values */}
      <section className="bg-black text-white py-32 mb-40" data-id="element-341">
        <div className="max-w-[1800px] mx-auto px-6" data-id="element-342">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16" data-id="element-343">
            {[{
            title: 'Cultural Authority',
            desc: 'Every pattern and weave is sourced with deep respect for its origin, ensuring the cultural integrity of our pieces remains untarnished.'
          }, {
            title: 'Ancestral Craft',
            desc: 'We collaborate with master weavers in Bonwire and artisans across Dakar to bring centuries-old techniques to life.'
          }, {
            title: 'Modern Luxury',
            desc: 'By blending traditional textiles with high-fashion tailoring, we create a new standard of luxury that is both timeless and timely.'
          }].map((pillar, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="space-y-6" data-id="element-344">
                <span className="text-xs font-display font-bold tracking-[0.4em] uppercase text-white/40" data-id="element-345">
                  0{i + 1}
                </span>
                <h3 className="text-xs md:text-sm font-display font-bold tracking-widest uppercase" data-id="element-346">
                  {pillar.title}
                </h3>
                <p className="text-sm font-normal tracking-widest leading-relaxed text-white/70 uppercase" data-id="element-347">
                  {pillar.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* The Atelier Section */}
      <section className="px-6 max-w-[1800px] mx-auto mb-40" data-id="element-348">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" data-id="element-349">
          <div className="lg:col-span-7 order-2 lg:order-1" data-id="element-350">
            <div className="grid grid-cols-2 gap-4" data-id="element-351">
              <motion.div {...fadeIn} className="aspect-[3/4] bg-grey-divider overflow-hidden" data-id="element-352">
                <img src="https://images.unsplash.com/photo-1598554747436-c9293d6a588f?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover grayscale" alt="Atelier detail" data-id="element-353" />
              </motion.div>
              <motion.div {...fadeIn} transition={{
              delay: 0.2
            }} className="aspect-[3/4] bg-grey-divider overflow-hidden mt-12" data-id="element-354">
                <img src="https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&q=80&w=800" className="w-full h-full object-cover grayscale" alt="Fabric detail" data-id="element-355" />
              </motion.div>
            </div>
          </div>
          <motion.div {...fadeIn} className="lg:col-span-5 order-1 lg:order-2 space-y-8" data-id="element-356">
            <span className="text-xs md:text-sm font-display font-bold uppercase tracking-[0.5em] text-black/50 block" data-id="element-357">
              The Alexandria Studio
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-display font-bold tracking-widest uppercase leading-tight" data-id="element-358">
              Where Heritage <br data-id="element-359" /> is Hand-Finished
            </h2>
            <p className="text-sm md:text-base font-light tracking-widest text-black/70 leading-relaxed uppercase" data-id="element-360">
              Our studio in Alexandria, VA, serves as the heartbeat of our
              operations. It is here that our bespoke pieces are tailored to
              perfection and where our clients experience the tactile beauty of
              our collections firsthand.
            </p>
            <p className="text-sm md:text-base font-light tracking-widest text-black/70 leading-relaxed uppercase" data-id="element-361">
              Every garment undergoes a rigorous process of quality control and
              hand-finishing, ensuring that the piece you receive is a
              masterpiece of cultural craftsmanship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final Hook */}
      <section className="px-6 text-center py-20 border-t border-grey-divider" data-id="element-362">
        <motion.div {...fadeIn} data-id="element-363">
          <h2 className="text-2xl font-display font-bold tracking-[0.3em] uppercase mb-8" data-id="element-364">
            Join the Narrative
          </h2>
          <p className="max-w-xl mx-auto text-sm font-light tracking-widest text-black/60 uppercase mb-12" data-id="element-365">
            Experience the intersection of tradition and tomorrow. Explore our
            latest collections or book a private consultation.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6" data-id="element-366">
            <a href="/lookbook" className="bg-black text-white px-12 py-4 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-black/80 transition-colors" data-id="element-367">
              Explore Lookbook
            </a>
            <a href="/booking" className="border border-black px-12 py-4 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors" data-id="element-368">
              Book Consultation
            </a>
          </div>
        </motion.div>
      </section>
    </div>;
}